import { useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { IGetRowsParams, IDatasource } from "ag-grid-community";
import { useBookingKitSearchFilters } from "../../hooks/use-booking-kit-search-filters";
import { useDebouncedValue } from "@hooks";
import {
  BookingKitQueryInput,
  useBookingKitSearchLazyQuery,
  useBookingKitSearchTotalLazyQuery
} from "@bonliva-crm/graphql";
import { BookingKitSortBy, SortOrder } from "@bonliva-crm/shared";

type Props = {
  getGridRef: () => AgGridReact;
};

export function useBookingKitTableDatasource(props: Props) {
  const { getGridRef } = props;

  const {
    queryInput,
    filterManager: {
      formMethods: { watch }
    }
  } = useBookingKitSearchFilters();

  const debouncedInput = useDebouncedValue(queryInput, 400);

  const queryInputRef = useRef<BookingKitQueryInput>();

  const [getItems] = useBookingKitSearchLazyQuery();
  const [getTotal] = useBookingKitSearchTotalLazyQuery();

  // const sortValue = watch("sort");

  useEffect(() => {
    queryInputRef.current = queryInput;
  }, [queryInput]);

  const sortValue = watch("sort");

  const getSort = () => {
    switch (sortValue) {
      case "createdOn_asc":
        return { sortBy: BookingKitSortBy.CreatedOn, sortOrder: SortOrder.Asc };
      case "createdOn_desc":
        return { sortBy: BookingKitSortBy.CreatedOn, sortOrder: SortOrder.Desc };
      case "bookingDate_asc":
        return { sortBy: BookingKitSortBy.BookingDate, sortOrder: SortOrder.Asc };
      case "bookingDate_desc":
        return { sortBy: BookingKitSortBy.BookingDate, sortOrder: SortOrder.Desc };
      case "timereportCreatedOn_asc":
        return {
          sortBy: BookingKitSortBy.TimereportCreatedOn,
          sortOrder: SortOrder.Asc
        };
      case "timereportCreatedOn_desc":
        return {
          sortBy: BookingKitSortBy.TimereportCreatedOn,
          sortOrder: SortOrder.Desc
        };
      case "timereportApprovedOn_asc":
        return {
          sortBy: BookingKitSortBy.TimereportApprovedOn,
          sortOrder: SortOrder.Asc
        };
      case "timereportApprovedOn_desc":
        return {
          sortBy: BookingKitSortBy.TimereportApprovedOn,
          sortOrder: SortOrder.Desc
        };

      default:
        console.log("Unknown sort value", sortValue);
    }
  };

  const getRows = (rowParams: IGetRowsParams) => {
    const filter = queryInputRef.current;
    // const filter = {};

    const limit = rowParams.endRow - rowParams.startRow;
    const skip = rowParams.startRow;

    Promise.all([
      getItems({
        variables: {
          limit,
          skip,
          filter,
          ...getSort()
        },
        fetchPolicy: "no-cache"
      }),
      getTotal({
        variables: { filter },
        fetchPolicy: "no-cache"
      })
    ])
      .then((res) => {
        rowParams.successCallback(
          res[0].data.bookingKitSearch.items,
          res[1].data.bookingKitSearchTotal.total
        );
      })
      .catch((err) => {
        console.error(err);
        rowParams.failCallback();
      });
  };

  const dataSource: IDatasource = useMemo(() => {
    return {
      rowCount: undefined,
      getRows,
      destroy: () => {
        const grid = getGridRef();
        if (!grid?.api?.isDestroyed()) {
          grid?.api.getSelectedNodes()?.forEach((node) => {
            node.setSelected(false);
          });
        }
      }
    };
  }, [debouncedInput]);

  return dataSource;
}
